import { FC } from 'react';
import { useMaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import { CustomTable } from 'src/presentations/components/molecules/table/Table';
import { formatDate } from 'src/utils/date';
import { beautifyEnum } from 'src/utils/utility';
import { MatchingServiceDto } from 'src/generated/services/TFinancialApi';

export interface IShippingService {
  id?: string;
  polpod: string;
  shipperName: string;
  consigneeName: string;
  service: string;
  type: string;
  carrier: string;
  incoterm: string;
  etd: string;
}

export interface IReviewContractCharges {
  chargeType: string;
  chargeNameOrCategory: string;
  contractUnitPrice: string;
}

const useColumns = (mismatchedFields: string[] = []): MRT_ColumnDef<MatchingServiceDto>[] => {
  const isMismatched = (field: string) => mismatchedFields.includes(field);

  const getCellStyle = (field: string) => ({
    color: isMismatched(field) ? 'red' : 'inherit',
  });

  return [
    {
      enableEditing: false,
      accessorKey: 'polpod',
      header: 'Trade Lane',
      accessorFn: (row) => {
        const isTradeLaneMismatched = isMismatched('route') || isMismatched('pol') || isMismatched('pod');
        return (
          <span style={{ color: isTradeLaneMismatched ? 'red' : 'inherit' }}>{row?.listZone?.map((item) => `${item?.pol} / ${item?.pod}`)?.join(', ')}</span>
        );
      },
    },
    {
      enableEditing: false,
      accessorKey: 'shipperName',
      header: 'Shipper Name',
      accessorFn: (row) => <span style={getCellStyle('shipper name')}>{row?.shipperName ? beautifyEnum(row.shipperName).toUpperCase() : '-'}</span>,
    },
    {
      enableEditing: false,
      accessorKey: 'consigneeName',
      header: 'Consignee Name',
      accessorFn: (row) => <span style={getCellStyle('consignee name')}>{row?.consigneeName ? beautifyEnum(row.consigneeName).toUpperCase() : '-'}</span>,
    },
    {
      header: 'Billed Entity',
      enableEditing: false,
      accessorKey: 'billingPartyCode',
      accessorFn: (row) => (
        <span style={getCellStyle('billingPartyCode')}>{row?.billingPartyCode ? beautifyEnum(row.billingPartyCode).toUpperCase() : '-'}</span>
      ),
    },
    {
      enableEditing: false,
      accessorKey: 'service',
      header: 'Service',
      accessorFn: (row) => <span style={getCellStyle('service')}>{row?.service || '-'}</span>,
    },
    {
      enableEditing: false,
      accessorKey: 'type',
      header: 'Type',
      accessorFn: (row) => <span style={getCellStyle('type')}>{row?.type || '-'}</span>,
    },
    {
      enableEditing: false,
      accessorKey: 'modality',
      header: 'Modality',
      accessorFn: (row) => <span style={getCellStyle('modality')}>{row?.modality || '-'}</span>,
    },
    {
      enableEditing: false,
      accessorKey: 'carrier',
      header: 'Carrier',
      accessorFn: (row) => <span style={getCellStyle('carrier')}>{row?.carrier || '-'}</span>,
    },
    {
      enableEditing: false,
      accessorKey: 'incoterm',
      header: 'Incoterm',
      accessorFn: (row) => <span style={getCellStyle('incoterm')}>{row?.incoterm || '-'}</span>,
    },
    {
      header: 'ETD',
      id: 'etd',
      accessorFn: (row) => {
        const isEtdMismatched = isMismatched('validity period') || isMismatched('etd');
        const etdValue = row?.estimatedDepartureDate ? formatDate(new Date(row.estimatedDepartureDate)) : '-';
        return <span style={{ color: isEtdMismatched ? 'red' : 'inherit' }}>{etdValue}</span>;
      },
      enableEditing: false,
    },
  ];
};

interface IUnrecognizedTableProps {
  data: MatchingServiceDto[];
  mismatchedFields?: string[];
}

export const TableUnrecognizedService: FC<IUnrecognizedTableProps> = (props) => {
  const { data = [], mismatchedFields = [] } = props;
  const columns = useColumns(mismatchedFields);

  const table = useMaterialReactTable({
    enableSorting: false,
    enablePinning: false,
    enableRowDragging: false,
    enableColumnActions: false,
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableEditing: false,
    editDisplayMode: 'table',
    enableRowSelection: false,
    enableMultiRowSelection: false,
    data,
    columns,
    muiTableProps: {
      sx: {
        borderCollapse: 'separate',
      },
    },
    muiTableBodyCellProps: {
      sx: {
        borderBottom: '1px solid #DFDFDF !important',
      },
    },
    muiTableHeadCellProps: {
      sx: {
        borderBottom: '1px solid #DFDFDF !important',
      },
    },
  });

  return <CustomTable table={table} />;
};
